import { useMemo, useState, useRef } from 'react';
import { PaymentsProp } from '@wetags/payments';
import { useGetPatientBalancesSummaryQuery } from '@frontend/api-collections';
import { Commands1 } from '@frontend/api-commands';
import { useTranslation } from '@frontend/i18n';
import { paymentsSentry } from '@frontend/payments-hooks';
import { breakpoints, useMatchMedia } from '@frontend/responsiveness';
import { useAppScopeStore } from '@frontend/scope';
import { useAlert, useModalControl } from '@frontend/design-system';
import { useBulkCollectionsFilterShallowStore } from '../../../store/bulk-collections-filter-store';
import { useBulkCollectionSelectAllShallowStore } from '../../../store/bulk-collections-select-all-store';
import { BulkCreateModal, BulkCreateStepId } from '../BulkCreateModal/bulk-create-modal';
import { BULK_CREATE_STEPS } from '../BulkCreateModal/bulk-create-steps-key-title';
import { useValidateUsersShallowStore } from '../BulkCreateModal/stores/validate-users-store';
import { SyncedAtText } from '../common/SyncedAtText';
import { getFilterString } from '../utils';
import { CollectionsSummary } from './collections-summary';
import { Filters } from './filters';
import { DesktopList } from './list/desktop-list';
import { MobileList } from './list/mobile-list';
import { styles } from './styles';
import { MobileToolBar } from './top-toolbar/mobile-toolbar';
import { useCollectionsLastSyncedAt } from './use-collections-last-synced-at';

type CollectionListProps = {
  onDismissBanner?: () => void;
};

export const CollectionList = ({ onDismissBanner }: CollectionListProps = {}) => {
  const { selectedLocationIds } = useAppScopeStore();
  const { t } = useTranslation('payments');
  const activeLocationId = selectedLocationIds[0];
  const [USE_NEW_COLLECTIONS_API, isLoadingWeTag] = PaymentsProp.useWetag(
    'enable-payments-collections-batch',
    activeLocationId
  );
  const alert = useAlert();
  const isMobile = useMatchMedia({ maxWidth: breakpoints.small.max });
  const [selectedPatientsIds, setSelectedPatientsIds] = useState<string[]>([]);
  const [modalInitialStep, setModalInitialStep] = useState<BulkCreateStepId>(BULK_CREATE_STEPS.SEND_SMS.id);
  const { excluded } = useValidateUsersShallowStore('excluded');
  const { handleBulkExclusion } = useBulkCollectionSelectAllShallowStore('handleBulkExclusion');
  const { triggerProps, modalProps } = useModalControl({
    onClose: () => {
      handleBulkExclusion(excluded);
    },
  });
  const queryStartTimeRef = useRef<number>(Date.now());
  const { balanceType, searchValue } = useBulkCollectionsFilterShallowStore('balanceType', 'searchValue');

  const lastSyncCompletedAt = useCollectionsLastSyncedAt({ selectedLocationIds });

  const { data: collections, isLoading } = Commands1.Queries.useGetPatientsBalanceQuery({
    request: {
      locationIds: selectedLocationIds,
    },
    options: {
      enabled: !USE_NEW_COLLECTIONS_API && !isLoadingWeTag,
      onError: () => alert.error('Error fetching collections'),
      refetchOnMount: 'always',
      onSuccess: () => {
        const queryTime = Date.now() - queryStartTimeRef.current;
        paymentsSentry.log('Collections query completed', {
          queryTimeMs: queryTime,
          locations: JSON.stringify(selectedLocationIds),
        });
      },
    },
  });

  const { data: bannerInfo } = useGetPatientBalancesSummaryQuery({
    request: {
      locationIds: selectedLocationIds,
      filter: getFilterString(balanceType, searchValue),
    },
    options: {
      enabled: USE_NEW_COLLECTIONS_API && !isLoadingWeTag && selectedLocationIds.length > 0,
      onError: () => alert.error(t('Error fetching banner info')),
      refetchOnMount: 'always',
    },
  });

  const totalAmountToCollect = useMemo(() => {
    if (USE_NEW_COLLECTIONS_API) {
      return bannerInfo?.totalAmount;
    }
    return collections?.patientBalances?.reduce((acc, collection) => acc + (collection.balance ?? 0), 0);
  }, [collections, bannerInfo, USE_NEW_COLLECTIONS_API]);

  const toggleSelection = (patientId: string) => {
    setSelectedPatientsIds((prev) => {
      if (prev.includes(patientId)) {
        return prev.filter((p) => p !== patientId);
      } else {
        return [...prev, patientId];
      }
    });
  };

  return (
    <div css={[styles.collectionList.container, isMobile && styles.collectionList.containerMobile]}>
      {isMobile && (
        <div css={styles.collectionList.syncInfo}>
          <SyncedAtText lastSyncCompletedAt={lastSyncCompletedAt} />
        </div>
      )}
      <Filters />
      <CollectionsSummary balance={totalAmountToCollect} />
      {isMobile ? (
        <>
          {collections?.patientBalances && (
            <MobileToolBar
              count={selectedPatientsIds.length}
              onClear={() => {
                setSelectedPatientsIds([]);
              }}
              onSelectAll={() => {
                setSelectedPatientsIds(
                  collections.patientBalances?.filter((pb) => !!pb.phoneNumber).map((pb) => pb.patientId) || []
                );
              }}
              selectionState={
                selectedPatientsIds.length === collections.patientBalances.length
                  ? 'all'
                  : selectedPatientsIds.length > 0
                  ? 'some'
                  : 'none'
              }
              isSelectionDisabled={collections.patientBalances.filter((pb) => !!pb.phoneNumber).length === 0}
              triggerProps={triggerProps}
              toggleInitialStep={setModalInitialStep}
            />
          )}
          <MobileList
            isLoading={isLoading}
            collections={collections}
            toggleSelection={toggleSelection}
            selected={selectedPatientsIds}
            useNewApi={USE_NEW_COLLECTIONS_API}
            triggerProps={triggerProps}
            toggleInitialStep={setModalInitialStep}
          />
        </>
      ) : (
        <DesktopList
          isLoading={isLoading}
          collections={collections}
          selected={selectedPatientsIds}
          onSelectionChange={setSelectedPatientsIds}
          triggerProps={triggerProps}
          toggleInitialStep={setModalInitialStep}
          onDismissBanner={onDismissBanner}
          useNewApi={USE_NEW_COLLECTIONS_API}
        />
      )}
      {modalProps.show && (
        <BulkCreateModal
          initialStep={modalInitialStep}
          locationIds={selectedLocationIds}
          modalProps={modalProps}
          selected={collections?.patientBalances?.filter((pb) => selectedPatientsIds.includes(pb.patientId)) || []}
          useNewApi={USE_NEW_COLLECTIONS_API}
        />
      )}
    </div>
  );
};
